// components/GlobalActivityMonitor.tsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import clsx from 'clsx';
import SimpleLoader from './ui/SimpleLoader';
import {
  ShieldAlert,
  Building,
  Funnel,
  Table2,
  Presentation,
  Calendar,
  LayoutGrid,
  CheckCircle,
  EyeOff,
  AudioLines,
} from '@/components/ui/lucide-memo';
import { DesignerIcon } from '@/components/ui/CustomIcons';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { portalViewWrapper } from './portal/portalTheme';
import { formatCreditsInteger } from '#lib/formatCredits';
import tableStyles from './portal/Table.module.css';
import { TABLE_GLASS_HEADER, TABLE_GLASS_ROW } from './portal/tableSurface';
import { InventoryListToolbar } from './portal/InventoryListToolbar';
import { HeadlineStats, type HeadlineStat } from './portal/HeadlineStats';
import { useTableColumns } from './portal/useTableColumns';
import type { ActivityFeedResult, ActivityItem, ActivityModule } from '#types/types';
import SourceUsageReport from './SourceUsageReport';
import DaimonTranscript from './DaimonChatsReport';
import { GenericEntitySelector } from './portal/GenericEntitySelector';

interface GlobalActivityMonitorProps {
  isActive: boolean;
  /** Closes this standalone view. Owned by the Portal, so it is passed in. */
  onExit?: () => void;
}

/** The same icons the left nav uses for each module, so the column reads without a legend. */
const MODULE_ICON: Record<ActivityModule, React.ReactNode> = {
  studio: <DesignerIcon size={15} strokeWidth={1.6} />,
  source: <Funnel size={15} strokeWidth={1.6} />,
  analyst: <Table2 size={15} strokeWidth={1.6} />,
  slides: <Presentation size={15} strokeWidth={1.6} />,
  daimon: <AudioLines size={15} strokeWidth={1.6} />,
};

const MODULE_LABEL: Record<ActivityModule, string> = {
  studio: 'Studio',
  source: 'Source',
  slides: 'Slides',
  analyst: 'Analyst',
  daimon: 'Daimon',
};

const MODULE_ORDER: ActivityModule[] = ['studio', 'source', 'slides', 'analyst', 'daimon'];

/** How each module's per-row figure is derived, so an approximate number is not read as exact. */
const CREDITS_HINT: Record<ActivityModule, string> = {
  studio: 'Exact: the generation that produced this version, joined via its task snapshot.',
  source: "Spend on this campaign's property across its run window. Approximate: GenerationLog has no campaign id, so runs overlapping on one property share a figure.",
  slides: 'Spend by this deck\'s project within the selected period. Approximate: several rounds in one window share the figure.',
  analyst: 'Not attributable.',
  daimon: "Her voice answers by this user between the conversation's first and last message. Approximate: answers carry no conversation id. Her web research bills under Assist.",
};

const CREDITS_UNATTRIBUTED_HINT: Record<ActivityModule, string> = {
  studio: 'No task snapshot links this version to a generation, so its cost cannot be attributed.',
  source: 'No billing found on this campaign\'s property during its run window.',
  slides: 'No spend recorded for this deck in the selected period.',
  analyst: 'Analyst turns bill with no project and no property, so GenerationLog cannot attribute them to a session.',
  daimon: 'No voice answer billed for this user while the conversation ran.',
};

interface ActivityColumn {
  id: string;
  header: string;
  /** Default width, and the floor the resize hook sizes from. */
  minWidth: number;
  align?: 'right';
}

const COLUMNS: ActivityColumn[] = [
  { id: 'when', header: 'When', minWidth: 90 },
  { id: 'module', header: 'Module', minWidth: 70 },
  { id: 'who', header: 'Who', minWidth: 180 },
  { id: 'activity', header: 'Activity', minWidth: 400 },
  { id: 'credits', header: 'Credits', minWidth: 90, align: 'right' },
  { id: 'organisation', header: 'Organisation', minWidth: 190 },
];

const SORT_OPTIONS = [
  { label: 'Most recent', field: 'time' },
  { label: 'Organisation', field: 'organisation' },
  { label: 'Module', field: 'module' },
];

const PERIOD_LABEL: Record<string, string> = {
  today: 'Today',
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
};

const DEFAULT_PERIOD = 'today';

/**
 * Poll cadence. This view cannot be pushed to: the socket room is scoped to the VIEWER's own
 * organisation (useSocketManager joins with its own organisationId, and emitToOrganisation targets
 * the entity's org room), so a Fater admin never receives the client-org events this feed exists to
 * show. Polling is therefore the mechanism, and the inherited 120s made it look frozen.
 * Paused while the tab is hidden, and refetched immediately on return.
 */
const POLL_INTERVAL_MS = 15000;
/**
 * Floors between reads, by what triggered them. A floor bounds the GAP; using one value for
 * everything let automatic triggers sustain a rate far above the intended cadence, because the
 * socket fires on every leasing event batch in a busy organisation.
 *   auto   - interval and socket. Held at the poll cadence so the sustained rate cannot exceed it.
 *   return - tab focus or visibility. Short, so coming back feels immediate without letting
 *            focus churn burst.
 *   user   - the refresh button, a period or filter change. No floor: it is explicit intent.
 */
const FETCH_FLOOR_MS: Record<'auto' | 'return' | 'user', number> = {
  auto: POLL_INTERVAL_MS,
  return: 3000,
  user: 0,
};

/**
 * Module scope on purpose. A component-scoped ref resets on remount, and a remount both clears
 * the ref and re-runs the mount fetch, so remount churn (dev Fast Refresh, or any parent
 * re-key) would be entirely unthrottled. This survives it.
 */
let lastFetchAt = 0;
/**
 * The period/mask the last fetch actually used, also module-scoped. It separates the two things
 * that re-run the mount effect: a genuine filter change, which MUST refetch because the data
 * differs, from a remount with identical parameters, which must not.
 */
let lastFetchKey = '';

/** Compact, fixed-width-ish stamps. date-fns' "about 1 hour ago" was noise in a dense table. */
const shortWhen = (iso: string) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '-';
  const minutes = Math.floor((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const OrganisationLogo: React.FC<{ orgId: string; orgName: string }> = React.memo(({ orgId, orgName }) => {
  const [failedOrgId, setFailedOrgId] = useState<string | null>(null);
  const handleError = useCallback(() => setFailedOrgId(orgId), [orgId]);

  if (failedOrgId === orgId) {
    return <Building size={14} className="flex-shrink-0 text-white/30" />;
  }

  return (
    <img
      src={`/api/organisation/logo?orgId=${orgId}`}
      alt={`${orgName} logo`}
      className="h-4 w-4 flex-shrink-0 object-contain grayscale"
      onError={handleError}
    />
  );
});
OrganisationLogo.displayName = 'OrganisationLogo';

const ActivityRow = React.memo(
  ({
    item,
    columns,
    onOpen,
    transcripts = false,
    open = false,
  }: {
    item: ActivityItem;
    columns: ActivityColumn[];
    onOpen: (item: ActivityItem) => void;
    /** A Daimon conversation opens on its transcript under the row, for those allowed to read it. */
    transcripts?: boolean;
    open?: boolean;
  }) => {
    const daimon = item.module === 'daimon' && Boolean(item.subjectId) && transcripts;
    const canOpen = daimon || Boolean(item.subjectId && item.subjectEntityType);
    // Outcome is carried by weight, not hue: a failed or running row sits back from a done one.
    const isSettled = item.outcome === 'ok';

    const cells: Record<string, React.ReactNode> = {
      when: (
        <span className="truncate tabular-nums text-white/40" style={{ fontSize: '12px' }}>
          {shortWhen(item.at)}
        </span>
      ),
      module: (
        <span
          className="flex text-white/50"
          title={MODULE_LABEL[item.module]}
          aria-label={MODULE_LABEL[item.module]}
          role="img"
        >
          {MODULE_ICON[item.module]}
        </span>
      ),
      who: (
        <span className="truncate text-white/75" style={{ fontSize: '13px' }} title={item.userName ?? ''}>
          {item.userName ?? <span className="text-white/30">Unattributed</span>}
        </span>
      ),
      activity: (
        <>
          {item.imageUrl && (
            <img
              src={item.imageUrl}
              alt=""
              className="mr-2.5 h-7 w-11 flex-shrink-0 rounded-[4px] border border-white/[0.06] object-cover"
            />
          )}
          <div className="min-w-0">
            <div className="flex min-w-0 items-baseline gap-1.5">
              <span className="flex-shrink-0 text-white/40" style={{ fontSize: '13px' }}>
                {item.verb}
              </span>
              <span
                className={clsx('truncate', isSettled ? 'text-white/90' : 'text-white/55')}
                style={{ fontSize: '13px' }}
                title={item.subject}
              >
                {item.subject}
              </span>
            </div>
            {item.detail && (
              <div className="truncate text-white/30" style={{ fontSize: '11px' }} title={item.detail}>
                {item.detail}
              </div>
            )}
          </div>
        </>
      ),
      credits:
        item.credits === null ? (
          <span
            className="text-white/20"
            style={{ fontSize: '12px' }}
            title={CREDITS_UNATTRIBUTED_HINT[item.module]}
          >
            -
          </span>
        ) : (
          <span
            className="font-mono tabular-nums text-white/70"
            style={{ fontSize: '12px' }}
            title={CREDITS_HINT[item.module]}
          >
            {formatCreditsInteger(item.credits)}
          </span>
        ),
      organisation: (
        <>
          {item.organisationId && (
            <OrganisationLogo orgId={item.organisationId} orgName={item.organisationName ?? ''} />
          )}
          <span className="ml-2 truncate text-white/50" style={{ fontSize: '12px' }}>
            {item.organisationName ?? '-'}
          </span>
        </>
      ),
    };

    return (
      <div
        data-table-row-id={item.id}
        className={clsx(tableStyles.gridRow, tableStyles.bodyRow, TABLE_GLASS_ROW, !canOpen && '!cursor-default')}
        style={{ minWidth: 'var(--table-min-width, 0)' }}
        onClick={canOpen ? () => onOpen(item) : undefined}
        aria-expanded={daimon ? open : undefined}
      >
        {columns.map((column) => (
          <div
            key={column.id}
            className={clsx(tableStyles.cell, column.align === 'right' && 'justify-end')}
          >
            {cells[column.id]}
          </div>
        ))}
      </div>
    );
  }
);
ActivityRow.displayName = 'ActivityRow';

const GlobalActivityMonitor: React.FC<GlobalActivityMonitorProps> = ({ isActive, onExit }) => {
  const { kernel } = useKernel();
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setError] = useState<{ key: string; message: string } | null>(null);
  const [loadedFeed, setFeed] = useState<(ActivityFeedResult & { selectionKey: string }) | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Record<string, unknown>>({});
  const [sortField, setSortField] = useState('time');
  const [sortDir, setSortDir] = useState('desc');
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const socketLastEventTimestamp = useKernelStore((state) => state.socketLastEventTimestamp);
  const requestRef = useRef<{ key: string; controller: AbortController } | null>(null);
  const identityKey = useKernelStore((state) => JSON.stringify([state.userContext.dbUserId, state.userContext.authenticatedOrganisationId,
    state.userContext.isImpersonating, state.userContext.trueUser?.email, state.userContext.isFaterAdmin, state.userContext.isOrganisationAdmin]));
  const canSelectSourceOrganisation = useKernelStore((state) => state.userContext.isFaterAdmin);
  // `lastFetchAt` is module-scoped and survives unmount, but `feed` does not. Without this the
  // throttle floor would skip the very first fetch after a remount, and since the render gate
  // and both recovery effects sit behind `hasLoaded`, the panel would stay blank forever.
  const hasFeedRef = useRef(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const { orderedColumns, getWidth, startResize, autoSize, getReorderProps, draggingId, dropTargetId } =
    useTableColumns(COLUMNS);

  // Deliberately not memoised: widths live in a ref inside the hook, so a resize re-renders
  // without changing any dependency a useMemo could watch, and the grid would freeze mid-drag.
  // Six columns of string building per render is nothing next to the rows.
  const gridVars = {
    '--table-grid-cols': orderedColumns.map((column) => `${getWidth(column)}fr`).join(' '),
    '--table-min-width': `${orderedColumns.reduce((sum, column) => sum + getWidth(column), 0)}px`,
  } as React.CSSProperties;

  const period = (filters.period as string) || DEFAULT_PERIOD;
  // Absent = hidden. Our own usage is noise here by default; admins watch clients.
  const showFater = filters.internal === 'shown';
  const sourceView = filters.view === 'source';
  // Every user's Daimon conversations: private, so the chip shows to Fater admins alone.
  const sourceEnvironment = (filters.sourceEnvironment as string) || 'production';
  const sourceOrganisationId = sourceView && canSelectSourceOrganisation ? filters.organisation as string | undefined : undefined;
  const requestKey = JSON.stringify([period, showFater, sourceView, sourceEnvironment, sourceOrganisationId, identityKey]);
  const feed = loadedFeed?.selectionKey === requestKey ? loadedFeed : null;
  const error = loadError?.key === requestKey ? loadError.message : null;

  const [openDaimon, setOpenDaimon] = useState<string | null>(null);
  const handleOpen = useCallback(
    (item: ActivityItem) => {
      if (item.module === 'daimon' && item.subjectId) { setOpenDaimon((current) => (current === item.subjectId ? null : item.subjectId)); return; }
      const k = kernel.current;
      if (!k || !item.subjectId || !item.subjectEntityType) return;
      k.focusEntityUI(item.subjectEntityType as any, item.subjectId);
      // Close the panel too, or it stays on top of the thing just focused.
      onExit?.();
    },
    [kernel, onExit]
  );

  const handleSortChange = useCallback(
    (field: string) => {
      setSortDir((prev) => (field === sortField ? (prev === 'asc' ? 'desc' : 'asc') : 'desc'));
      setSortField(field);
    },
    [sortField]
  );

  const fetchFeed = useCallback(
    async (showLoader: boolean, trigger: 'auto' | 'return' | 'user' = 'auto') => {
      // Floor first, so a throttled automatic read never blocks on an in-flight one. A first
      // paint is exempt: it is not a throttleable repeat, and skipping it strands the panel
      // blank with no retry path (close a row and reopen Activity inside 3s).
      if (hasFeedRef.current && lastFetchKey === requestKey && Date.now() - lastFetchAt < FETCH_FLOOR_MS[trigger]) return;
      if (requestRef.current?.key === requestKey) return;
      requestRef.current?.controller.abort();
      const request = { key: requestKey, controller: new AbortController() };
      requestRef.current = request;
      lastFetchAt = Date.now();
      lastFetchKey = requestKey;
      if (showLoader) setIsLoading(true);
      try {
        const response = await axios.post<ActivityFeedResult>('/api/admin', {
          action: 'getActivityFeed',
          timeRange: period,
          includeFater: showFater,
          ...(sourceView ? { sourceUsage: true, sourceEnvironment, sourceOrganisationId } : {}),
        }, { signal: request.controller.signal });
        if (requestRef.current !== request || request.controller.signal.aborted) return;
        setFeed({ ...response.data, selectionKey: request.key });
        setError(null);
        setLastUpdatedAt(Date.now());
      } catch (err: any) {
        if (requestRef.current !== request || request.controller.signal.aborted) return;
        console.error('Failed to fetch activity feed:', err);
        setError({ key: request.key, message: err.response?.data?.message ||
            (typeof err.response?.data === 'string' ? err.response.data : null) ||
            err.message ||
            'An unknown error occurred fetching activity.' });
      } finally {
        if (requestRef.current === request && !request.controller.signal.aborted) {
          requestRef.current = null;
          hasFeedRef.current = true;
          setIsLoading(false);
          setHasLoaded(true);
        }
      }
    },
    [period, showFater, sourceView, sourceEnvironment, sourceOrganisationId, requestKey]
  );

  useEffect(() => {
    if (!isActive) {
      hasFeedRef.current = false;
      // eslint-disable-next-line react-hooks/set-state-in-effect -- clearing hasLoaded when the view deactivates IS this effect's purpose, so the next activation fetches instead of showing a stale feed.
      setHasLoaded(false);
      return;
    }
    // A changed period or mask is real intent and must always fetch, since the rows differ.
    // Identical parameters mean this is a remount, which is throttled like any return.
    fetchFeed(true, requestKey === lastFetchKey ? 'return' : 'user');
    return () => {
      requestRef.current?.controller.abort();
      requestRef.current = null;
    };
  // requestKey covers every fetch selection, including authenticated identity.
  }, [isActive, requestKey]);

  useEffect(() => {
    if (!isActive || !hasLoaded) return;

    const tick = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      fetchFeed(false, 'auto');
    };
    const interval = setInterval(tick, POLL_INTERVAL_MS);

    // Coming back to the tab should not wait out a whole interval.
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchFeed(false, 'return');
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  // requestKey changes replace the polling callback without depending on a function.
  }, [isActive, hasLoaded, requestKey]);

  // Own-organisation events do arrive over the socket, so react to them at once rather than
  // waiting for the next poll. Cross-org activity still depends on the interval above.
  useEffect(() => {
    if (!isActive || !hasLoaded || !socketLastEventTimestamp) return;
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reacting to an own-organisation socket event by refreshing IS this effect's purpose.
    fetchFeed(false, 'auto');
  // Deliberately keyed on the socket timestamp alone. isActive and hasLoaded are already
  // guarded in the body, and the two effects above own those transitions, so listing them
  // here would refetch twice. fetchFeed stays out: functions do not belong in a dependency
  // array (AGENTS.md 4.4).
  }, [socketLastEventTimestamp]);

  /** Organisation values come from the data, so the picker only ever offers real orgs. */
  const organisationValues = useMemo(() => {
    if (!feed) return [];
    const names = new Map<string, string>();
    for (const item of feed.items) {
      if (item.organisationId && item.organisationName) names.set(item.organisationId, item.organisationName);
    }
    for (const row of feed.sourceUsage?.rows ?? []) names.set(row.organisationId, row.organisationName);
    return [...names.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [feed]);

  const filterOptions = useMemo(
    () => [
      {
        key: 'period',
        label: 'Period',
        icon: <Calendar size={16} strokeWidth={1.9} />,
        values: Object.entries(PERIOD_LABEL).map(([value, label]) => ({ value, label })),
      },
      {
        key: 'module',
        label: 'Module',
        icon: <LayoutGrid size={16} strokeWidth={1.9} />,
        values: MODULE_ORDER.map((module) => ({ value: module, label: MODULE_LABEL[module] })),
      },
      {
        key: 'outcome',
        label: 'Outcome',
        icon: <CheckCircle size={16} strokeWidth={1.9} />,
        values: [
          { value: 'ok', label: 'Done' },
          { value: 'running', label: 'Running' },
          { value: 'failed', label: 'Failed' },
        ],
      },
      {
        key: 'organisation',
        label: 'Organisation',
        icon: <Building size={16} strokeWidth={1.9} />,
        values: organisationValues,
      },
      {
        key: 'internal',
        label: 'Internal usage',
        icon: <EyeOff size={16} strokeWidth={1.9} />,
        values: [
          { value: 'hidden', label: 'Hidden' },
          { value: 'shown', label: 'Shown' },
        ],
      },
    ],
    [organisationValues]
  );

  useEffect(() => {
    if (!isActive) return;
    const ticker = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(ticker);
  }, [isActive]);

  const visibleItems = useMemo(() => {
    if (!feed) return [];
    const term = search.trim().toLowerCase();
    const moduleFilter = filters.module as string | undefined;
    const outcomeFilter = filters.outcome as string | undefined;
    const orgFilter = filters.organisation as string | undefined;

    const rows = feed.items.filter((item) => {
      if (moduleFilter && item.module !== moduleFilter) return false;
      if (outcomeFilter && item.outcome !== outcomeFilter) return false;
      if (orgFilter && item.organisationId !== orgFilter) return false;
      if (term) {
        const haystack = `${item.userName ?? ''} ${item.subject} ${item.organisationName ?? ''} ${item.verb}`;
        if (!haystack.toLowerCase().includes(term)) return false;
      }
      return true;
    });

    const direction = sortDir === 'asc' ? 1 : -1;
    const byTime = (a: ActivityItem, b: ActivityItem) =>
      (new Date(a.at).getTime() - new Date(b.at).getTime()) * direction;

    return [...rows].sort((a, b) => {
      if (sortField === 'organisation') {
        const compared = (a.organisationName ?? '').localeCompare(b.organisationName ?? '') * direction;
        return compared !== 0 ? compared : byTime(a, b);
      }
      if (sortField === 'module') {
        const compared = a.module.localeCompare(b.module) * direction;
        return compared !== 0 ? compared : byTime(a, b);
      }
      return byTime(a, b);
    });
  }, [feed, search, filters, sortField, sortDir]);

  const stats = useMemo<HeadlineStat[]>(() => {
    if (!feed || sourceView) return [];
    const orgFilter = filters.organisation as string | undefined;
    // One sentence: the period's total first, then only the modules that spent (a row of zeros
    // said nothing the total did not).
    const total = MODULE_ORDER.reduce((sum, module) => sum + feed.credits[module], feed.credits.assist);
    const entries: HeadlineStat[] = [{ value: formatCreditsInteger(total), label: 'credits this period' }];
    for (const moduleKey of MODULE_ORDER) {
      if (feed.credits[moduleKey] > 0) entries.push({ value: formatCreditsInteger(feed.credits[moduleKey]), label: MODULE_LABEL[moduleKey] });
    }
    if (feed.credits.assist > 0) entries.push({ value: formatCreditsInteger(feed.credits.assist), label: 'Other chats' });
    if (orgFilter) {
      entries.push({
        value: formatCreditsInteger(feed.orgCredits[orgFilter] ?? 0),
        label: 'Selected org',
      });
    }
    return entries;
  }, [feed, filters, sourceView]);

  const sourceReport = useMemo(() => {
    if (!feed?.sourceUsage) return null;
    const term = search.trim().toLowerCase();
    const direction = sortDir === 'asc' ? 1 : -1;
    const rows = feed.sourceUsage.rows.filter((row) => !term || `${row.userName} ${row.organisationName}`.toLowerCase().includes(term));
    rows.sort((a, b) => (sortField === 'organisation' ? a.organisationName.localeCompare(b.organisationName) * direction : 0)
      || a.date.localeCompare(b.date) * direction || a.userName.localeCompare(b.userName));
    return { ...feed.sourceUsage, rows };
  }, [feed, search, sortField, sortDir]);

  const currentSortLabel = (sourceView ? SORT_OPTIONS.filter((option) => option.field !== 'module') : SORT_OPTIONS).find((option) => option.field === sortField)?.label ?? 'Most recent';

  // Re-rendered by the freshness ticker below, so "updated Ns ago" actually counts up.
  const toolbarStatus = (
    <span className="flex items-center gap-3">
      {feed && feed.presence.length > 0 && (
        <span
          className="text-white/45"
          style={{ fontSize: '12px' }}
          title={feed.presence.map((entry) => `${entry.userName} (${entry.organisationName ?? '-'})`).join('\n')}
        >
          {feed.presence.length} active in the last hour
        </span>
      )}
      {/* A capped read is stated, never implied. Without this the view looked exhaustive while
          silently missing the older part of whichever source was busiest. */}
      {feed?.completeSince && (
        <span
          className="text-amber-300/70"
          style={{ fontSize: '12px' }}
          title={`More activity exists in this period than one read returns. Everything from the time shown is present; older rows were not loaded. Narrow the period or filter by organisation to see them.`}
        >
          complete since {new Date(feed.completeSince).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      )}
      {lastUpdatedAt && feed && (
        <span
          className="text-white/30"
          style={{ fontSize: '12px' }}
          title={`Refreshes every ${POLL_INTERVAL_MS / 1000}s while this tab is visible. Cross-organisation activity cannot be pushed over the socket, so it is polled.`}
        >
          updated {Math.max(0, Math.round((nowTick - lastUpdatedAt) / 1000))}s ago
        </span>
      )}
    </span>
  );

  if (!isActive || (!hasLoaded && !isLoading && !error)) return null;

  return (
    // Not ViewSection: it is an overflow-y-auto scroll container, which clipped the filter and
    // sort popovers and let the header scroll away. Source tables use this flex shell instead ,
    // header and toolbar pinned, only the rows scroll, so popovers can escape upward.
    <div
      className={clsx(portalViewWrapper, 'font-sans')}
      style={{ fontFamily: 'ui-sans-serif, -apple-system, system-ui, sans-serif' }}
    >
        {/* ChatGPT's page header (owner, 2026-09-28): the page names itself, the period sits in
            its sentence, the views are chips. The breadcrumb band and its back arrow are gone. */}
        <InventoryListToolbar
          title={`Activity · ${PERIOD_LABEL[period] ?? 'All time'}`}
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder={sourceView ? 'Search users' : 'Search activity'}
          refreshIsLoading={isLoading}
          onManualRefresh={() => fetchFeed(true, 'user')}
          beforeFilterContent={<div className="flex max-w-full flex-wrap items-center gap-1">
            <button type="button" aria-pressed={!sourceView} onClick={() => setFilters((previous) => ({ ...previous, view: 'feed' }))} className={clsx('h-8 rounded-full px-3 transition-colors', !sourceView ? 'bg-white/[0.08] text-white' : 'text-[#E5E5E5] hover:bg-white/[0.05]')} style={{ fontSize: 14 }}>Activity feed</button>
            <button type="button" aria-pressed={sourceView} onClick={() => setFilters((previous) => ({ ...previous, view: 'source' }))} className={clsx('h-8 rounded-full px-3 transition-colors', sourceView ? 'bg-white/[0.08] text-white' : 'text-[#E5E5E5] hover:bg-white/[0.05]')} style={{ fontSize: 14 }}>Source usage</button>
            {!sourceView && <button type="button" role="switch" aria-checked={showFater} onClick={() => setFilters((previous) => ({ ...previous, internal: showFater ? 'hidden' : 'shown' }))} title="Fater's own activity, the Daimon's conversations included" className={clsx('flex h-8 items-center gap-2 rounded-full px-3 transition-colors', showFater ? 'bg-white/[0.08] text-white' : 'text-[#E5E5E5] hover:bg-white/[0.05]')} style={{ fontSize: 14 }}>
              <span aria-hidden className={clsx('relative h-4 w-7 rounded-full transition-colors duration-150', showFater ? 'bg-[#ECECEC]' : 'bg-[#444444]')}><span className={clsx('absolute top-0.5 h-3 w-3 rounded-full transition-all duration-150', showFater ? 'left-[14px] bg-[#0D0D0D]' : 'left-0.5 bg-[#AFAFAF]')} /></span>
              Fater users
            </button>}
            {sourceView && <select aria-label="Source environment" value={sourceEnvironment} onChange={(event) => setFilters((previous) => ({ ...previous, sourceEnvironment: event.target.value }))} className="h-8 max-w-[24vmin] rounded-full border border-[#2A2A2A] bg-transparent px-3 text-[#E5E5E5]" style={{ fontSize: 14 }}>
              <option value="production">Production</option><option value="preprod">Preprod</option><option value="preview">Preview</option>
            </select>}
            {sourceView && canSelectSourceOrganisation && <GenericEntitySelector
              type="Organisation" selectedIds={sourceOrganisationId ? [sourceOrganisationId] : []} variant="row" title="Select Source organisation"
              onSelect={(id) => setFilters((previous) => ({ ...previous, organisation: id }))}
              onClearSelection={() => setFilters((previous) => ({ ...previous, organisation: undefined }))}
              renderTrigger={(selected, open) => <button type="button" aria-label="Select Source organisation" onClick={open} className="h-8 rounded-full px-3 text-[#E5E5E5] transition-colors hover:bg-white/[0.08]" style={{ fontSize: 14 }}>
                {sourceOrganisationId ? selected[0]?.name ?? 'Selected organisation' : 'All organisations'}
              </button>}
            />}
          </div>}
          filterOptions={sourceView ? filterOptions.filter((option) => !['module', 'outcome', 'organisation'].includes(option.key)) : filterOptions}
          viewFilters={filters}
          isFilterKeyLocked={() => false}
          mergeWithLockedFilters={(candidate) => candidate}
          setFilters={(next) => {
            const cleaned = { ...next };
            if (cleaned.internal === 'hidden') delete cleaned.internal;
            setFilters(cleaned);
          }}
          sortOptions={sourceView ? SORT_OPTIONS.filter((option) => option.field !== 'module') : SORT_OPTIONS}
          activeSortField={sortField}
          activeSortDir={sortDir}
          onSortChange={handleSortChange}
          currentSortLabel={currentSortLabel}
          rightToolbarContent={toolbarStatus}
        />

        {/* Own row, below the toolbar, the same slot the inventory metrics bar occupies. */}
        {stats.length > 0 && (
          <div className="flex shrink-0 items-center gap-4 overflow-x-auto px-6 py-3 custom-scrollbar">
            <span
              className="shrink-0 whitespace-nowrap text-white/40"
              style={{ fontSize: '11px' }}
              title="Credits spent across the selected period. Not affected by the module, outcome or organisation filters, which only narrow the rows below."
            >
              Credits this period
            </span>
            <HeadlineStats stats={stats} className="w-max" />
          </div>
        )}

        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {(isLoading || (!feed && !error)) && (
            <div className="flex min-h-[260px] items-center justify-center">
              <SimpleLoader message="Loading activity..." />
            </div>
          )}

          {!isLoading && error && (
            <div className="m-4 flex min-h-[200px] flex-col items-center justify-center rounded-[14px] border border-white/10 bg-white/[0.02] p-6 text-center">
              <ShieldAlert size={30} className="mb-3 text-white/40" />
              <p className="font-semibold text-white/80" style={{ fontSize: '14px' }}>
                Failed to load activity
              </p>
              <p className="mt-1 text-white/50" style={{ fontSize: '13px' }}>
                {error}
              </p>
            </div>
          )}

          {!isLoading && !error && sourceReport && <SourceUsageReport report={sourceReport} />}

          {!sourceView && !isLoading && !error && feed && visibleItems.length === 0 && (
            <div className="p-12 text-center italic text-white/20" style={{ fontSize: '13px' }}>
              No results found.
            </div>
          )}

          {!sourceView && !isLoading && !error && visibleItems.length > 0 && (
            <div
              ref={tableContainerRef}
              className={clsx(tableStyles.tableContainer, tableStyles.pageInset, tableStyles.glassSurface, '!border-transparent !bg-transparent')}
              style={gridVars}
            >
              <div className={clsx(tableStyles.gridRow, tableStyles.headerRow, TABLE_GLASS_HEADER)}>
                {orderedColumns.map((column, index) => (
                  <div
                    key={column.id}
                    {...getReorderProps(column.id)}
                    className={clsx(
                      tableStyles.cell,
                      'relative cursor-grab active:cursor-grabbing',
                      column.align === 'right' && 'justify-end',
                      draggingId === column.id && 'opacity-40',
                      dropTargetId === column.id && 'bg-white/[0.06]'
                    )}
                    title="Drag to reorder"
                  >
                    <span className="truncate">{column.header}</span>
                    <div
                      role="separator"
                      aria-orientation="vertical"
                      title="Drag to resize. Double-click to auto-size."
                      draggable={false}
                      className="absolute right-0 top-0 z-20 h-full w-2 cursor-col-resize hover:bg-white/10"
                      onDragStart={(event) => event.preventDefault()}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        // Widths are fr units, so convert the pixel delta or the grip drifts.
                        const totalUnits = orderedColumns.reduce((sum, c) => sum + getWidth(c), 0);
                        const pixelWidth = tableContainerRef.current?.clientWidth || totalUnits;
                        startResize(column.id, event.clientX, getWidth(column), totalUnits / pixelWidth);
                      }}
                      onDoubleClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        autoSize(column.id, tableContainerRef.current, index, orderedColumns.length);
                      }}
                    />
                  </div>
                ))}
              </div>
              {visibleItems.map((item) => (
                <React.Fragment key={item.id}>
                  <ActivityRow item={item} columns={orderedColumns} onOpen={handleOpen} transcripts={canSelectSourceOrganisation} open={openDaimon === item.subjectId} />
                  {item.module === 'daimon' && canSelectSourceOrganisation && openDaimon === item.subjectId && item.subjectId
                    ? <DaimonTranscript sessionId={item.subjectId} userName={item.userName ?? 'User'} /> : null}
                </React.Fragment>
              ))}
            </div>
          )}
      </div>
    </div>
  );
};

export default GlobalActivityMonitor;
